import { useAppSelector } from '@customer/store/hooks';
import { SelectionTimeRange, TimeRange } from '@customer/store/slices/globalSlice';
import { TFunction } from 'i18next';
import { DateTime, Duration } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseDurationFromSeconds } from '@cas/hooks/dateTimeFormatting';

const localeFormat: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED,
  month: 'short',
};
const timeAndDateWithMillisecondsFormat: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_SECONDS,
  hour12: false,
  fractionalSecondDigits: 3,
};
const timeAndDateWithSecondsFormat: Intl.DateTimeFormatOptions = {
  ...DateTime.DATETIME_MED_WITH_SECONDS,
  hour12: false,
};

const getTimeRangeText = (timeRange: TimeRange, t: TFunction): string => {
  switch (timeRange) {
    case SelectionTimeRange.Months3:
      return t('LastMonths', { val: 3 });
    case SelectionTimeRange.Months2:
      return t('LastMonths', { val: 2 });
    case SelectionTimeRange.Days30:
      return t('LastDays', { val: 30 });
    case SelectionTimeRange.Days7:
      return t('LastDays', { val: 7 });
    case SelectionTimeRange.Hours24:
      return t('LastHours', { val: 24 });
  }
};

export interface LocaleFormatter {
  globalTimeRange: string;
  showTimeAndDate: (dt: DateTime) => string;
  showDateOnly: (dt: DateTime) => string;
  showTimeOnly: (dt: DateTime, hideSeconds?: boolean) => string;
  showTimeOnlyFromMillis: (millis: number) => string;
  showHourOnly: (dt: DateTime) => string;
  showRelative: (dt: DateTime) => string | null;
  showTimeZoneOffset: () => string;
  showTimeAndDateFromIso: (isoTime: string) => string;
  showTimeAndDateWithMillisecondsFromIso: (isoTime: string) => string;
  showTimeAndDateWithSecondsFromIso: (isoTime: string) => string;
  showDateOnlyFromIso: (isoTime: string) => string;
  showTimeOnlyFromIso: (isoTime: string, hideSeconds?: boolean) => string;
  showDurationFromSeconds: (seconds: number) => string;
  showRelativeFromIso: (isoTime: string) => string | null;
  parseFromIso: (isoTime: string) => DateTime;
  parseDurationFromSeconds: (seconds: number) => Duration;
  getTimeRangeText: (timeRange: TimeRange, t: TFunction) => string;
  getCustomTimeRangeText: (start: string, end: string) => string;
  getNumberText: (value: number, fractionDigits?: number) => string;
  getNumberPercentText: (
    value: number,
    fractionDigits?: number,
    alreadyNormalized?: boolean,
  ) => string;
  showTimeAndDateWithSecondsFromSeconds: (seconds: number) => string;
  showTimeAndDateWithSecondsFromMillis: (millis: number) => string;
  showTimeAndDateWithMillisFromMillis: (millis: number) => string;
}

export const useLocaleFormatter = (): LocaleFormatter => {
  const { t } = useTranslation();

  const userData = useAppSelector(state => state.auth);
  const timeRange = useAppSelector(state => state.global.timeRange);

  const currentGlobalTimeRange = useMemo<string>(() => {
    return getTimeRangeText(timeRange, t);
  }, [t, timeRange]);

  const parseDurationFromSecondsCB = useCallback(
    (seconds: number) => {
      return parseDurationFromSeconds(seconds, userData.language);
    },
    [userData.language],
  );

  const getCustomTimeRangeElement = useCallback(
    (isoTime: string): string => {
      const dt = DateTime.fromISO(isoTime, { zone: userData.siteTimezone });
      return dt.toLocaleString(
        { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
        { locale: userData.dateTimeFormat },
      );
    },
    [userData.siteTimezone, userData.dateTimeFormat],
  );

  const getNumberPercentText = (
    value: number,
    fractionDigits?: number,
    alreadyNormalized?: boolean,
  ) => {
    // Value might already be converted to percentage
    const adjustedValue = alreadyNormalized === true ? value * 0.01 : value;

    return new Intl.NumberFormat(userData.dateTimeFormat, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: fractionDigits !== undefined ? fractionDigits : 1,
    }).format(adjustedValue);
  };

  // Relative times and durations shown in LANGUAGE, not time/date format since they contain human-readable text (e.g. "minutes ago").
  return {
    globalTimeRange: currentGlobalTimeRange,
    showTimeAndDate: (dt: DateTime) =>
      dt.toLocaleString(localeFormat, { locale: userData.dateTimeFormat }),
    showDateOnly: (dt: DateTime) =>
      dt.toLocaleString(DateTime.DATE_MED, { locale: userData.dateTimeFormat }),
    showTimeOnly: (dt: DateTime, hideSeconds?: boolean) =>
      dt.toLocaleString(hideSeconds ? DateTime.TIME_SIMPLE : DateTime.TIME_WITH_SECONDS, {
        locale: userData.dateTimeFormat,
      }),
    showTimeOnlyFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, {
        zone: userData.siteTimezone,
      }).toLocaleString(
        { ...DateTime.TIME_WITH_SECONDS, fractionalSecondDigits: 3 },
        { locale: userData.dateTimeFormat },
      ),
    showHourOnly: (dt: DateTime) => dt.setLocale(userData.dateTimeFormat).toFormat('t'),
    showRelative: (dt: DateTime) => dt.setLocale(userData.language).toRelative(),
    showTimeAndDateFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone }).toLocaleString(localeFormat, {
        locale: userData.dateTimeFormat,
      }),
    showTimeAndDateWithMillisecondsFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone }).toLocaleString(
        timeAndDateWithMillisecondsFormat,
        { locale: userData.dateTimeFormat },
      ),
    showTimeAndDateWithSecondsFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone }).toLocaleString(
        timeAndDateWithSecondsFormat,
        { locale: userData.dateTimeFormat },
      ),
    showDateOnlyFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone }).toLocaleString(DateTime.DATE_MED, {
        locale: userData.dateTimeFormat,
      }),
    showTimeOnlyFromIso: (isoTime: string, hideSeconds?: boolean) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone }).toLocaleString(
        hideSeconds ? DateTime.TIME_SIMPLE : DateTime.TIME_WITH_SECONDS,
        {
          locale: userData.dateTimeFormat,
        },
      ),
    showDurationFromSeconds: (seconds: number) =>
      parseDurationFromSecondsCB(seconds).toHuman({ unitDisplay: 'narrow' }),
    showTimeZoneOffset: () =>
      DateTime.local()
        .setZone(userData.siteTimezone)
        .setLocale(userData.dateTimeFormat)
        .toFormat('ZZ'),
    showRelativeFromIso: (isoTime: string) =>
      DateTime.fromISO(isoTime, { zone: userData.siteTimezone })
        .setLocale(userData.language)
        .toRelative(),
    parseFromIso: (isoTime: string) => DateTime.fromISO(isoTime, { zone: userData.siteTimezone }),
    parseDurationFromSeconds: parseDurationFromSecondsCB,
    getTimeRangeText,
    getCustomTimeRangeText: (start: string, end: string) =>
      `${getCustomTimeRangeElement(start)} - ${getCustomTimeRangeElement(end)}`,
    getNumberText: (value: number, fractionDigits?: number) =>
      fractionDigits !== undefined
        ? value.toLocaleString(userData.dateTimeFormat, {
            maximumFractionDigits: fractionDigits,
          })
        : value.toLocaleString(userData.dateTimeFormat),
    getNumberPercentText,
    showTimeAndDateWithSecondsFromSeconds: (seconds: number) =>
      DateTime.fromSeconds(seconds, {
        zone: userData.siteTimezone,
      }).toLocaleString(timeAndDateWithSecondsFormat, {
        locale: userData.dateTimeFormat,
      }),
    showTimeAndDateWithSecondsFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, {
        zone: userData.siteTimezone,
      }).toLocaleString(timeAndDateWithSecondsFormat, {
        locale: userData.dateTimeFormat,
      }),
    showTimeAndDateWithMillisFromMillis: (millis: number) =>
      DateTime.fromMillis(millis, {
        zone: userData.siteTimezone,
      }).toLocaleString(
        { ...DateTime.DATETIME_MED_WITH_SECONDS, fractionalSecondDigits: 3 },
        { locale: userData.dateTimeFormat },
      ),
  };
};
